/**
 * Spec Phase 5 Slice 5D — overwrite-confirm modal.
 *
 * Surfaced when the section-generate endpoint returns 409
 * section_not_empty. Per Phase 5 brief §6:
 *   "Section already has 3 paragraphs. Regenerate (replaces all)
 *    or Cancel?"
 *
 * Lightweight: no shadcn dialog dependency — same render surface
 * as the matter-card delete confirm pattern in Home.tsx so the
 * frontend test infra (Jest + Testing Library) doesn't need a
 * portal/animation primitive to assert on.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';

interface Props {
  sectionTitle: string;
  paragraphCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isGenerating: boolean;
  /** True when the drafter has typed steering instructions
   *  before clicking Regenerate. Softens the copy from "won't
   *  be recoverable" alarm to "preserved in audit history" so
   *  drafters mid-anchor-coverage workflow don't bail. */
  hasSteering?: boolean;
  /** Offered when the drafter has steering AND the section is not
   *  empty: apply the instruction to the existing draft instead of
   *  replacing it. Routes to the paragraph-preserving nl-edit
   *  endpoint, which diffs per paragraph rather than DELETE-then-
   *  INSERT. Omit to render the dialog exactly as before. */
  onMerge?: () => void;
}

export default function OverwriteConfirmDialog({
  sectionTitle,
  paragraphCount,
  onConfirm,
  onCancel,
  isGenerating,
  hasSteering = false,
  onMerge,
}: Props): JSX.Element {
  // The merge route only makes sense for "apply this instruction to
  // what is there" — with no instruction there is nothing to merge.
  const canMerge = Boolean(onMerge) && hasSteering;
  const title = canMerge
    ? 'Apply your instruction — how?'
    : hasSteering
      ? 'Regenerate with steering?'
      : `Regenerate ${sectionTitle}?`;
  const body = canMerge ? (
    <>
      This section has {paragraphCount} paragraph
      {paragraphCount === 1 ? '' : 's'}. Merging edits them in place
      and keeps everything your instruction doesn&rsquo;t touch.
      Rewriting drafts the section again from scratch, which can
      drop material the current draft covers.
    </>
  ) : hasSteering ? (
    <>
      This section has {paragraphCount} paragraph
      {paragraphCount === 1 ? '' : 's'}. Regenerating will apply
      your steering instructions and replace the existing draft.
      The previous version is preserved in the audit history.
    </>
  ) : (
    <>
      This section already has {paragraphCount} paragraph
      {paragraphCount === 1 ? '' : 's'}. Regenerating replaces
      all existing content. The previous text won&rsquo;t be
      recoverable from the editor (audit history is preserved
      in the diagnostic panel).
    </>
  );
  const confirmLabel = hasSteering
    ? 'Regenerate with steering →'
    : 'Regenerate';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm regenerate"
      data-testid="spec-overwrite-confirm"
    >
      <div className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 shadow-xl">
        <h2
          className="mb-2 text-base font-semibold text-zinc-900"
          data-testid="spec-overwrite-title"
        >
          {title}
        </h2>
        <p
          className="mb-6 text-sm text-zinc-600"
          data-testid="spec-overwrite-body"
        >
          {body}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={isGenerating}
            data-testid="spec-overwrite-cancel"
          >
            Cancel
          </button>
          {canMerge ? (
            <>
              {/* Rewrite stays one click away, but it is the
                * destructive option and no longer the only one, so
                * it reads as secondary. */}
              <button
                type="button"
                className="inline-flex h-9 items-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                onClick={onConfirm}
                disabled={isGenerating}
                data-testid="spec-overwrite-confirm-btn"
              >
                Rewrite section
              </button>
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1 rounded-sm bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
                onClick={onMerge}
                disabled={isGenerating}
                data-testid="spec-overwrite-merge-btn"
              >
                {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
                Merge into current draft
              </button>
            </>
          ) : (
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1 rounded-sm bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
              onClick={onConfirm}
              disabled={isGenerating}
              data-testid="spec-overwrite-confirm-btn"
            >
              {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
